import React, { FC } from 'react';
import { Trans } from 'react-i18next';
import { Button, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';

interface IProps {
  name: keyof IEmptyState;
  fullWidth?: boolean;
}

interface IStateOptions {
  image: string;
  text: string;
  action?: string;
}

export interface IEmptyState {
  timeline: IStateOptions;
  groupJoin: IStateOptions;
  groupMember: IStateOptions;
  groupApproval: IStateOptions;
  groupScheduled: IStateOptions;
  groupRemoved: IStateOptions;
  search: IStateOptions;
  notificationPop: IStateOptions;
  notifications: IStateOptions;
  groupsRecommended: IStateOptions;
  groupsFollowed: IStateOptions;
  groupsOwned: IStateOptions;
  groupsDepartments: IStateOptions;
  groupsLocations: IStateOptions;
  leaderboard: IStateOptions;
  peopleRecommended: IStateOptions;
  peopleFollowing: IStateOptions;
  peopleDepartments: IStateOptions;
  peopleLocations: IStateOptions;
  profileActivity: IStateOptions;
  myProfileActivity: IStateOptions;
  profileGroups: IStateOptions;
  myProfileGroups: IStateOptions;
  profileFollowing: IStateOptions;
  myProfileFollowing: IStateOptions;
  profileFollowers: IStateOptions;
  myProfileFollowers: IStateOptions;
  shareThese: IStateOptions;
  scheduled: IStateOptions;
  past: IStateOptions;
  myPosts: IStateOptions;
  suspendedUsers: IStateOptions;
  searchUsers: IStateOptions;
  messages: IStateOptions;
  dashboardContent: IStateOptions;
  invitations: IStateOptions;
}

const states: IEmptyState = {
  timeline: {
    image: '/images/mailbox.svg',
    text: 'timelineText',
  },
  groupJoin: {
    image: '/images/cards.svg',
    text: 'groupJoinText',
  },
  groupMember: {
    image: '/images/paper-airplane.svg',
    text: 'groupMemberText',
  },
  groupApproval: {
    image: '/images/checkboard.svg',
    text: 'groupApprovalText',
  },
  groupScheduled: {
    image: '/images/checkboard.svg',
    text: 'groupScheduledText',
  },
  groupRemoved: {
    image: '/images/trash.svg',
    text: 'groupRemovedText',
  },
  search: {
    image: '/images/spyglass.svg',
    text: 'searchText',
  },
  notificationPop: {
    image: '/images/notifications.svg',
    text: 'notificationPopText',
  },
  notifications: {
    image: '/images/notifications-2.svg',
    text: 'notificationsText',
  },
  groupsRecommended: {
    image: '/images/flower.svg',
    text: 'groupsRecommendedText',
  },
  groupsFollowed: {
    image: '/images/spyglass.svg',
    text: 'groupsFollowedText',
  },
  groupsOwned: {
    image: '/images/box-cards.svg',
    text: 'groupsOwnedText',
  },
  groupsDepartments: {
    image: '/images/box.svg',
    text: 'groupsDepartmentsText',
  },
  groupsLocations: {
    image: '/images/box.svg',
    text: 'groupsLocationsText',
  },
  peopleRecommended: {
    image: '/images/rockn-roll.svg',
    text: 'peopleRecommendedText',
  },
  peopleFollowing: {
    image: '/images/people.svg',
    text: 'peopleFollowingText',
  },
  peopleDepartments: {
    image: '/images/pipe.svg',
    text: 'peopleDepartmentsText',
  },
  peopleLocations: {
    image: '/images/pipe.svg',
    text: 'peopleLocationsText',
  },
  profileActivity: {
    image: '/images/mailbox.svg',
    text: 'profileActivityText',
  },
  myProfileActivity: {
    image: '/images/mailbox.svg',
    text: 'myProfileActivityText',
  },
  profileGroups: {
    image: '/images/box.svg',
    text: 'profileGroupsText',
  },
  myProfileGroups: {
    image: '/images/box.svg',
    text: 'myProfileGroupsText',
  },
  profileFollowing: {
    image: '/images/people-2.svg',
    text: 'profileFollowingText',
  },
  myProfileFollowing: {
    image: '/images/people-2.svg',
    text: 'myProfileFollowingText',
  },
  profileFollowers: {
    image: '/images/people-1.svg',
    text: 'profileFollowersText',
  },
  myProfileFollowers: {
    image: '/images/people-1.svg',
    text: 'myProfileFollowersText',
  },
  shareThese: {
    image: '/images/posts.svg',
    text: 'shareTheseText',
  },
  scheduled: {
    image: '/images/schedule.svg',
    text: 'scheduledText',
  },
  past: {
    image: '/images/posts.svg',
    text: 'pastText',
  },
  myPosts: {
    image: '/images/cards.svg',
    text: 'myPostsText',
  },
  suspendedUsers: {
    image: '/images/sofa.svg',
    text: 'suspendedUsersText',
  },
  searchUsers: {
    image: '/images/spyglass.svg',
    text: 'searchUsersText',
  },
  messages: {
    image: '/images/notifications.svg',
    text: 'messagesText',
  },
  dashboardContent: {
    image: '/images/folder.svg',
    text: 'adminDashboardContent',
  },
  leaderboard: {
    image: '/images/spyglass.svg',
    text: 'leaderboard',
  },
  invitations: {
    image: '/images/sofa.svg',
    text: 'invitationsText',
  },
};

const useStyles = makeStyles(theme => ({
  wrapper: {
    maxWidth: '50%',
    margin: '0 auto',
    textAlign: 'center',
  },
  fullWidth: {
    maxWidth: '100%',
  },
  imgWrapper: {
    display: 'block',
    margin: '0 auto',
    width: 252,
    height: 252,
  },
  img: {
    objectFit: 'contain',
  },
  text: {
    marginBottom: theme.spacing(3),
  },
}));

const EmptyState: FC<IProps> = ({ name, fullWidth = false }) => {
  const { image, text, action }: IStateOptions = states[name];
  const classes = useStyles();
  const wrapperClasses = classNames(classes.wrapper, {
    [classes.fullWidth]: fullWidth,
  });

  return (
    <div className={wrapperClasses}>
      <div className={classes.imgWrapper}>
        <img className={classes.img} src={image} />
      </div>
      <Typography className={classes.text} paragraph variant="subtitle2" align="center">
        <Trans i18nKey={`emptyStates:${text}`} />
      </Typography>
      {action && (
        <Button variant="outlined">
          <Trans i18nKey={`emptyStates:${action}`} />
        </Button>
      )}
    </div>
  );
};

export default EmptyState;
